import { useEffect, useId } from 'react';
import type { ButtonHTMLAttributes, InputHTMLAttributes, PropsWithChildren, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

export function Spinner() { return <span className="spin" role="status" aria-label="Cargando" />; }

export function Loading({ label = 'Cargando…' }: { label?: string }) {
  return <div className="loading"><Spinner />{label}</div>;
}

/** Placeholder de carga con shimmer: `rows` bloques `.skeleton` a `height`
    (px por defecto). El delay escalonado hace que la banda viaje en cascada
    en vez de barrer todas las filas a la vez. Para filas de lista pasa una
    height propia — <Skeleton height={48}/> simula un renglón de .list. */
export function Skeleton({ rows = 3, height = 14 }: { rows?: number; height?: number | string }) {
  const h = typeof height === 'number' ? `${height}px` : height;
  return <div className="skeleton-rows" aria-hidden="true">
    {Array.from({ length: Math.max(1, rows) }, (_, i) =>
      <div key={i} className="skeleton" style={{ height: h, animationDelay: `${i * 90}ms` }} />)}
  </div>;
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'secondary' | 'danger' | 'ghost'; small?: boolean; busy?: boolean };
export function Btn({ tone = 'primary', small, busy, disabled, children, ...rest }: BtnProps) {
  return <button className={`btn ${tone}${small ? ' small' : ''}`} disabled={disabled || busy} aria-busy={busy || undefined} {...rest}>
    {busy && <Spinner />}{children}
  </button>;
}

export function Badge({ tone = 'muted', children }: PropsWithChildren<{ tone?: 'ok' | 'warn' | 'bad' | 'info' | 'muted' }>) {
  return <span className={`badge ${tone}`}>{children}</span>;
}

export function Card({ children, flat, className }: PropsWithChildren<{ flat?: boolean; className?: string }>) {
  return <section className={`card${flat ? ' flat' : ''}${className ? ` ${className}` : ''}`}>{children}</section>;
}

export function CardTitle({ children, detail, icon }: PropsWithChildren<{ detail?: ReactNode; icon?: IconName }>) {
  return <><h2 className="card-title">{icon && <Icon name={icon} size={19} />}{children}</h2>{detail && <p className="card-detail">{detail}</p>}</>;
}

/** Encabezado de sección del portal (h1 + descripción + acciones a la derecha). */
export function SecHead({ title, desc, actions }: { title: ReactNode; desc?: ReactNode; actions?: ReactNode }) {
  return <header className="sec-head">
    <div><h1 className="sec-title">{title}</h1>{desc && <p className="sec-desc">{desc}</p>}</div>
    {actions && <div className="actions">{actions}</div>}
  </header>;
}

/** Cifra grande + etiqueta (tablero y estado del servidor); `warn` la marca en
    ámbar — `tone` generaliza el acento (warn/bad/ok/muted) y gana sobre `warn`.
    Con `icon` y `hint` se vuelve tarjeta KPI: icono + etiqueta small-caps +
    número tabular + pista. */
export function Stat({ n, l, warn, tone, icon, hint }: { n: number | string; l: string; warn?: boolean; tone?: 'warn' | 'bad' | 'ok' | 'muted'; icon?: IconName; hint?: string }) {
  const t = tone ?? (warn ? 'warn' : undefined);
  return <div className={`stat${t ? ` ${t}` : ''}`}>
    <div className="stat-top">{icon && <Icon name={icon} size={15} />}<span className="lbl">{l}</span></div>
    <div className="num">{n}</div>
    {hint && <div className="hint">{hint}</div>}
  </div>;
}

// ---------- Iconos: mismas metáforas de Ionicons de la app, trazo 1.8 ----------
export type IconName =
  'grid' | 'route' | 'truck' | 'doc' | 'people' | 'pin' | 'sliders' | 'building' |
  'person' | 'clipboard' | 'list' | 'pulse' | 'sun' | 'moon' | 'logout' | 'tray' |
  'check' | 'shield' | 'mail' | 'phone' | 'arrowRight' | 'eye' | 'eyeOff' | 'bell' |
  'lock' | 'clock' | 'key' | 'chevronRight' | 'plus' | 'cube' | 'snow' | 'wrench' |
  'camera' | 'pencil' | 'flag' | 'alert' | 'panelLeft';

const ICONS: Record<IconName, ReactNode> = {
  grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
  route: <><circle cx="6" cy="6" r="2.4" /><circle cx="18" cy="18" r="2.4" /><path d="M8.4 6H15a3 3 0 0 1 0 6H9a3 3 0 0 0 0 6h6.6" /></>,
  truck: <><path d="M2.5 6h10v8h-10z" /><path d="M12.5 9.5h3.5l3 3v2.5h-6.5z" /><circle cx="6.8" cy="16.6" r="1.7" /><circle cx="16.2" cy="16.6" r="1.7" /></>,
  doc: <><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4" /><path d="M9 12h6M9 16h6" /></>,
  people: <><circle cx="9" cy="8" r="3" /><path d="M3.5 19c.6-3 2.8-4.5 5.5-4.5s4.9 1.5 5.5 4.5" /><circle cx="16.8" cy="8.5" r="2.5" /><path d="M15.6 14.8c2.8.2 4.5 1.8 4.9 4.7" /></>,
  pin: <><path d="M12 21s-6.5-5.2-6.5-10.5a6.5 6.5 0 0 1 13 0C18.5 15.8 12 21 12 21z" /><circle cx="12" cy="10.5" r="2.3" /></>,
  sliders: <><path d="M7 4v5M7 13v7M12 4v9M12 17v3M17 4v3M17 11v9" /><circle cx="7" cy="11" r="2" /><circle cx="12" cy="15" r="2" /><circle cx="17" cy="9" r="2" /></>,
  building: <><path d="M5 21V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v17" /><path d="M3 21h18" /><path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2" /></>,
  person: <><circle cx="12" cy="8" r="3.2" /><path d="M5.5 20c.8-3.6 3.4-5.4 6.5-5.4s5.7 1.8 6.5 5.4" /></>,
  clipboard: <><rect x="5.5" y="4.5" width="13" height="16.5" rx="1.6" /><path d="M9 4.5V3h6v1.5" /><path d="M9.5 11.5l1.6 1.6 3.4-3.6" /><path d="M9.5 16.5h5" /></>,
  list: <><path d="M8.5 6.5h11M8.5 12h11M8.5 17.5h11" /><circle cx="4.8" cy="6.5" r="1.1" /><circle cx="4.8" cy="12" r="1.1" /><circle cx="4.8" cy="17.5" r="1.1" /></>,
  pulse: <><path d="M3.5 12h4l2.5-6.5 4 13L16.5 12h4" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2.2M12 19.3v2.2M4.7 4.7l1.6 1.6M17.7 17.7l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.7 19.3l1.6-1.6M17.7 6.3l1.6-1.6" /></>,
  moon: <><path d="M20 14.5A8.4 8.4 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5z" /></>,
  logout: <><path d="M13.5 4H6v16h7.5" /><path d="M10 12h10M17 8.5l3.5 3.5-3.5 3.5" /></>,
  tray: <><path d="M3.5 12.5 6.3 5h11.4l2.8 7.5V18a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 18z" /><path d="M3.5 12.5H9l1.1 2h3.8l1.1-2h5.5" /></>,
  check: <><path d="M4.5 12.5l5 5L19.5 7" /></>,
  shield: <><path d="M12 3l7 3v5.2c0 4.4-2.9 7.4-7 9.3-4.1-1.9-7-4.9-7-9.3V6z" /><path d="M9.2 11.6l2 2 3.6-3.8" /></>,
  mail: <><rect x="3" y="5.5" width="18" height="13.5" rx="1.8" /><path d="M3.5 7.5l8.5 6 8.5-6" /></>,
  phone: <><rect x="7" y="2.5" width="10" height="19" rx="2.2" /><path d="M10.5 18.5h3" /></>,
  arrowRight: <><path d="M4 12h15M13.5 6l6 6-6 6" /></>,
  eye: <><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" /><circle cx="12" cy="12" r="2.8" /></>,
  eyeOff: <><path d="M4.5 4.5l15 15" /><path d="M10.4 6A9.7 9.7 0 0 1 12 5.7c6 0 9.5 6.3 9.5 6.3a16.6 16.6 0 0 1-3 3.6M6.5 6.8C4 8.6 2.5 12 2.5 12S6 18.3 12 18.3c1.5 0 2.9-.4 4.1-1" /><path d="M9.9 10a2.9 2.9 0 0 0 4.1 4" /></>,
  bell: <><path d="M18 9.5a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6z" /><path d="M10 19.5a2.2 2.2 0 0 0 4 0" /></>,
  lock: <><rect x="5" y="10.5" width="14" height="10" rx="1.8" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.2v5l3.4 2" /></>,
  key: <><circle cx="8.5" cy="14.5" r="4" /><path d="M11.5 11.5L19.5 3.5M16 6l3 3M13.5 8.5l3 3" /></>,
  chevronRight: <path d="M9 5.5l6.5 6.5L9 18.5" />,
  plus: <path d="M12 5v14M5 12h14" />,
  cube: <><path d="M11 4.5a2 2 0 0 1 2 0l6.2 3.6a2 2 0 0 1 .9 1.7v7.4a2 2 0 0 1-.9 1.7L13 22.5a2 2 0 0 1-2 0l-6.2-3.6a2 2 0 0 1-.9-1.7V9.8a2 2 0 0 1 .9-1.7z" /><path d="M4.4 8.4 12 12.9l7.6-4.5M12 12.9v9" /></>,
  snow: <><circle cx="12" cy="12" r="2.3" /><path d="M12 2.5v4.4M12 17.1v4.4M3.8 7.3l3.8 2.2M16.4 14.5l3.8 2.2M20.2 7.3l-3.8 2.2M7.6 14.5l-3.8 2.2" /><path d="M12 2.5 10.3 4.3M12 2.5l1.7 1.8M12 21.5l-1.7-1.8M12 21.5l1.7-1.8" /></>,
  wrench: <><path d="M20.3 7.8a4.6 4.6 0 0 1-6 5.6l-7 7a2 2 0 0 1-2.8-2.9l7-7a4.6 4.6 0 0 1 5.6-6l-2.9 2.9 2.3 2.3z" /></>,
  camera: <><path d="M4 8.5h3l1.8-2.5h6.4L17 8.5h3a1.5 1.5 0 0 1 1.5 1.5v8.5a1.5 1.5 0 0 1-1.5 1.5H4A1.5 1.5 0 0 1 2.5 18.5V10A1.5 1.5 0 0 1 4 8.5z" /><circle cx="12" cy="14" r="3.4" /></>,
  pencil: <><path d="M14.5 5.5l4 4L8 20H4v-4z" /><path d="M12.5 7.5l4 4" /></>,
  flag: <><path d="M5.5 21V4" /><path d="M5.5 5h12l-2.5 4 2.5 4h-12" /></>,
  alert: <><path d="M12 3.5 2.5 19.5h19z" /><path d="M12 10v4M12 17.2v.3" /></>,
  panelLeft: <><rect x="3" y="4.5" width="18" height="15" rx="2.5" /><path d="M9.5 4.5v15" /></>,
};

export function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[name]}</svg>;
}

/** Botón cuadrado solo-icono (toggle de tema, salir) — siempre con etiqueta accesible. */
export function IconBtn({ label, icon, onClick, busy, className }: { label: string; icon: IconName; onClick(): void; busy?: boolean; className?: string }) {
  return <button type="button" className={`iconbtn${className ? ` ${className}` : ''}`} aria-label={label} title={label} disabled={busy} onClick={onClick}>
    <Icon name={icon} size={19} />
  </button>;
}

type FieldProps = { label: string; hint?: string } & InputHTMLAttributes<HTMLInputElement>;
export function Field({ label, hint, id, ...rest }: FieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return <div className="field"><label htmlFor={inputId}>{label}</label><input className="input" id={inputId} {...rest} />{hint && <span className="hint">{hint}</span>}</div>;
}

export function TextArea({ label, hint, id, ...rest }: { label: string; hint?: string } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return <div className="field"><label htmlFor={inputId}>{label}</label><textarea className="textarea" id={inputId} {...rest} />{hint && <span className="hint">{hint}</span>}</div>;
}

export function Select({ label, hint, id, children, ...rest }: PropsWithChildren<{ label: string; hint?: string } & SelectHTMLAttributes<HTMLSelectElement>>) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return <div className="field"><label htmlFor={inputId}>{label}</label><select className="select" id={inputId} {...rest}>{children}</select>{hint && <span className="hint">{hint}</span>}</div>;
}

export function Notice({ tone = 'info', icon, children, onClose }: PropsWithChildren<{ tone?: 'info' | 'warning' | 'danger'; icon?: IconName; onClose?: () => void }>) {
  // El role va en el cuerpo del mensaje, no en el contenedor: una live-region
  // (status/alert) no debe alojar controles interactivos como el botón ✕.
  return <div className={`notice ${tone}`}>
    {icon && <Icon name={icon} size={17} />}
    <span className="nbody" role={tone === 'danger' ? 'alert' : 'status'}>{children}</span>
    {onClose && <button className="close" onClick={onClose} aria-label="Cerrar aviso">✕</button>}
  </div>;
}

/** Lista vacía: icono + título + mensaje; `action` es el siguiente paso sugerido. */
export function Empty({ title, message, icon, action }: { title: string; message?: string; icon?: IconName; action?: ReactNode }) {
  return <div className="empty">
    <span className="empty-ic"><Icon name={icon ?? 'tray'} size={26} /></span>
    <span className="t">{title}</span>
    {message && <span>{message}</span>}
    {action && <div className="actions">{action}</div>}
  </div>;
}

// Pila global de modales por orden de montaje: solo el más reciente responde
// a Escape — sin esto, Esc sobre un diálogo anidado (el formulario de
// documento dentro de la ficha de unidad, el de adjunto dentro del viaje…)
// cerraba TODOS los niveles de golpe porque cada instancia escuchaba por su
// cuenta. El backdrop click ya es seguro sin la pila: el fondo del modal
// superior cubre al del padre.
const modalStack: number[] = [];
let modalSeq = 0;

export function Modal({ title, children, onClose, wide }: PropsWithChildren<{ title: string; onClose: () => void; wide?: boolean }>) {
  // Escape cierra el modal como en cualquier diálogo nativo, pero solo si es
  // el tope de la pila. Si una mutación sigue en vuelo, el modal se va pero
  // la acción termina y su aviso/error quedan en el banner.
  useEffect(() => {
    const id = ++modalSeq;
    modalStack.push(id);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalStack[modalStack.length - 1] === id) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const at = modalStack.indexOf(id);
      if (at >= 0) modalStack.splice(at, 1);
    };
  }, [onClose]);
  return <div className="modal-back" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
      <div className="row between modal-head"><h3>{title}</h3><Btn tone="ghost" small onClick={onClose}>Cerrar</Btn></div>
      <div className="modal-scroll">{children}</div>
    </div>
  </div>;
}
